import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  CloudDownload as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import authInstance from '../../services/httpClient';

interface Props {
  onBack: () => void;
}

interface BackupItem {
  key: string;
  storeId: string;
  action: string;
  timestamp: string;
  comment?: string;
  productSku?: string;
  productName?: string;
  sizeKb: number;
}

const ACCENT = '#e65100';

const formatDate = (timestamp: string) =>
  new Date(timestamp).toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const actionChipColor = (action: string): 'default' | 'success' | 'warning' | 'info' => {
  switch (action) {
    case 'manual':
      return 'default';
    case 'add-product':
      return 'success';
    case 'pre-restore':
      return 'warning';
    default:
      return 'info';
  }
};

const MenuBackupRestore: React.FC<Props> = ({ onBack }) => {
  const [storeId, setStoreId] = useState('');
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<BackupItem | null>(null);
  const [restoreResult, setRestoreResult] = useState<{ items: number; categories: number } | null>(null);

  const handleLoadBackups = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const storeParam = storeId.trim() ? `?storeId=${storeId.trim()}` : '';
      const response = await authInstance.get(
        `/admin/tools/uber-menu/backups${storeParam}`,
      );

      if (response.data.success) {
        setBackups(response.data.data || []);
      } else {
        setError(response.data.error || 'Failed to load backups');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const handleCreateBackup = useCallback(async () => {
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/backup',
        {
          storeId: storeId.trim() || undefined,
          comment: comment.trim() || undefined,
        },
      );

      if (response.data.success) {
        const backupKey = response.data.data?.key || response.data.data?.backupKey || 'unknown';
        setSuccess(`Backup created successfully. Key: ${backupKey}`);
        setComment('');
        // Refresh the backup list
        handleLoadBackups();
      } else {
        setError(response.data.error || 'Backup creation failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Backup creation failed');
    } finally {
      setCreating(false);
    }
  }, [storeId, comment, handleLoadBackups]);

  const handleRestore = async () => {
    if (!confirmRestore) return;

    setRestoring(true);
    setError(null);
    setSuccess(null);
    setRestoreResult(null);

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/restore',
        {
          storeId: storeId.trim() || undefined,
          backupKey: confirmRestore.key,
        },
      );

      if (response.data.success) {
        const data = response.data.data || {};
        setRestoreResult({
          items: data.itemCount || data.items || 0,
          categories: data.categoryCount || data.categories || 0,
        });
      } else {
        setError(response.data.error || 'Restore failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Restore failed');
      // Refresh backup list (restore creates a pre-restore backup)
      handleLoadBackups();
    } finally {
      setRestoring(false);
      setConfirmRestore(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={onBack} disabled={restoring || creating}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Menu Backup & Restore
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create snapshots of the current Uber Eats menu and restore from previous backups.
        Backups capture the full menu structure including items, categories, modifier groups,
        and pricing. Use this before making bulk changes or to roll back to a known-good state.
      </Typography>

      {/* Inputs */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Store ID Override (optional)"
              size="small"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              placeholder="Leave blank to use default store"
              fullWidth
              disabled={restoring || creating}
              helperText="Uber token and default store ID are auto-configured from AWS"
            />

            <Divider sx={{ my: 0.5 }} />

            {/* Action Buttons Row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <TextField
                  label="Backup Comment (optional)"
                  size="small"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="e.g. Before price update"
                  fullWidth
                  disabled={restoring || creating}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleCreateBackup}
                disabled={creating || restoring}
                startIcon={creating ? <CircularProgress size={18} /> : <BackupIcon />}
                sx={{
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#bf360c' },
                  height: 40,
                  minWidth: 160,
                }}
              >
                {creating ? 'Creating...' : 'Create Backup'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleLoadBackups}
                disabled={loading || restoring}
                startIcon={loading ? <CircularProgress size={18} /> : <DownloadIcon />}
                sx={{
                  borderColor: ACCENT,
                  color: ACCENT,
                  '&:hover': { borderColor: '#bf360c', bgcolor: '#fff3e0' },
                  height: 40,
                  minWidth: 160,
                }}
              >
                {loading ? 'Loading...' : 'Load Backups'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {restoreResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRestoreResult(null)}>
          Menu restored successfully. {restoreResult.items} items and{' '}
          {restoreResult.categories} categories applied to the live menu.
        </Alert>
      )}

      {/* Backup List Table */}
      {backups.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {backups.length} backup{backups.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              '& .MuiTableRow-root:hover': {
                bgcolor: '#fff3e0',
              },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Size
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.key} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(backup.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={backup.action}
                        size="small"
                        color={actionChipColor(backup.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {backup.productName ? (
                        <Box>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                            {backup.productName}
                          </Typography>
                          {backup.productSku && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {backup.productSku}
                            </Typography>
                          )}
                        </Box>
                      ) : backup.comment ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 280, fontStyle: 'italic' }}
                        >
                          {backup.comment}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          --
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {backup.sizeKb} KB
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setConfirmRestore(backup)}
                        disabled={restoring}
                        startIcon={<RestoreIcon />}
                        sx={{
                          borderColor: ACCENT,
                          color: ACCENT,
                          '&:hover': { borderColor: '#bf360c', bgcolor: '#fff3e0' },
                        }}
                      >
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Empty State */}
      {!loading && backups.length === 0 && !error && !success && !restoreResult && (
        <Alert severity="info" icon={<InfoIcon />}>
          Click Load Backups to view available menu snapshots,
          or Create Backup to save the current Uber menu.
        </Alert>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={!!confirmRestore}
        onClose={() => !restoring && setConfirmRestore(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Menu Restore</DialogTitle>
        <DialogContent>
          {confirmRestore && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will <strong>COMPLETELY REPLACE</strong> the current live menu on Uber
                Eats with the backup from{' '}
                <strong>{formatDate(confirmRestore.timestamp)}</strong>.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Backup:</strong> {formatDate(confirmRestore.timestamp)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Action:</strong>{' '}
                <Chip
                  label={confirmRestore.action}
                  size="small"
                  color={actionChipColor(confirmRestore.action)}
                  variant="outlined"
                />
              </Typography>
              {confirmRestore.comment && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Comment:</strong> {confirmRestore.comment}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Size:</strong> {confirmRestore.sizeKb} KB
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRestore(null)} disabled={restoring}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRestore}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={18} /> : <RestoreIcon />}
            sx={{
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#bf360c' },
            }}
          >
            {restoring ? 'Restoring...' : 'Restore Menu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuBackupRestore;
